import { useSearchParams } from "react-router";
import {
  BusinessInsights,
  parseTab,
  type Tab,
} from "@/design/BusinessInsights";
import { PageShell } from "@/design/ui";
import {
  parsePeriod,
  type CustomerInsights,
  type FinancialInsights,
} from "@/modules/insights/surfaces";
import { AppLink } from "~/components/AppLink";
import { BusinessPage } from "~/components/BusinessChrome";
import { ScreenPending } from "~/components/ScreenPending";
import { useQuery } from "~/lib/useQuery";

/**
 * BusinessInsightsScreen (docs/ux/screens/BusinessInsightsScreen.md).
 *
 * **The tab and the window live in the URL**, so the controls are plain links
 * and the data follows, exactly as they did on the server-rendered host. That
 * is what makes a dashboard state shareable ("look at the 30-day financials"),
 * and it is why the client parses the query string with **Insights' own**
 * parser rather than a copy: a URL this screen writes and one the API reads
 * must mean the same thing, and both fall back rather than refusing.
 *
 * Only the open tab is fetched. Two tabs, one query set each, never both.
 */
interface InsightsPayload {
  tab: Tab;
  businessName: string;
  customers: CustomerInsights | null;
  financials: FinancialInsights | null;
}

export function BusinessInsightsScreen() {
  const [params] = useSearchParams();
  const search = params.toString();
  const period = parsePeriod(params.get("period") ?? undefined);
  const tab = parseTab(params.get("tab"));

  const insights = useQuery<InsightsPayload>(
    `/business/insights${search ? `?${search}` : ""}`,
  );

  return (
    <BusinessPage>
      <PageShell>
        <div className="mx-auto w-full max-w-4xl">
          {insights.loading && !insights.data ? (
            <ScreenPending />
          ) : (
            <BusinessInsights
              tab={tab}
              period={period}
              businessName={insights.data?.businessName ?? ""}
              // The server sends only the open tab, so these are never both
              // set and the screen never has to choose between two answers.
              customers={insights.data?.customers ?? null}
              financials={insights.data?.financials ?? null}
              failed={insights.failure !== null || !insights.data}
              configured={insights.failure !== "not_configured"}
              Link={AppLink}
            />
          )}
        </div>
      </PageShell>
    </BusinessPage>
  );
}
